import React, { useRef, useState } from 'react';
import { uploadRentRollForPM } from '~/api/resident';
import { useFullScreenLoader } from '~/hooks/useFullScreenLoader';
import { toast } from 'react-toastify';
import { ReactComponent as FileUploadIcon } from '~/utils/images/file-upload-icon.svg';
import classes from './UploadRentRollPM.module.css';
import { AxiosError } from 'axios';

const ACCEPTED_EXTENSIONS = ['.csv', '.xls', '.xlsx', '.xlsm', '.xlsb'];

const isValidFileType = (file: File): boolean => {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
};

type UploadRentRollCsvPMProps = {
  buildingId: string;
};

const UploadRentRollCsvPM = ({ buildingId }: UploadRentRollCsvPMProps) => {
  const [selectedFile, setSelectedFile] = useState<File>();
  const [isUploading, setIsUploading] = useState(false);
  const { loader } = useFullScreenLoader();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      if (!isValidFileType(file)) {
        return toast.error('Invalid file type. Please upload a CSV or Excel file (.csv, .xls, .xlsx, .xlsm, .xlsb).');
      }
      setSelectedFile(file);
    }
  };

  const uploadFile = async () => {
    if (!selectedFile) {
      return toast.error('Please select a file to upload first');
    }

    const form = new FormData();
    form.append('buildingId', buildingId);
    form.append('file', selectedFile, selectedFile.name);

    setIsUploading(true);
    loader.show();

    try {
      await uploadRentRollForPM(form);
      toast.success('Rent roll uploaded successfully');
      setSelectedFile(undefined);
    } catch (e) {
      const error = e as AxiosError;
      toast.error(error.message);
    } finally {
      setIsUploading(false);
      loader.hide();
    }
  };

  return (
    <div className={classes.dropzoneWrapper}>
      <div
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`flex justify-center align-center ${classes.fileUploadWrapper}`}
      >
        <FileUploadIcon />
        <p className={classes.fileText}>
          {selectedFile?.name ? (
            selectedFile.name
          ) : (
            <span>
              Drop your file here or{' '}
              <span
                className={classes.browseText}
                onClick={() => inputRef.current?.click()}
              >
                browse
              </span>
              .
            </span>
          )}
        </p>
        <input
          type='file'
          accept='.csv,.xls,.xlsx,.xlsm,.xlsb'
          ref={inputRef}
          className={classes.sheetFileinput}
          onChange={(e) => {
            const file = e?.target?.files?.[0];
            if (!file) return;
            if (!isValidFileType(file)) {
              toast.error('Invalid file type. Please upload a CSV or Excel file (.csv, .xls, .xlsx, .xlsm, .xlsb).');
              e.target.value = '';
              return;
            }
            setSelectedFile(file);
          }}
        />
      </div>
      <button
        onClick={uploadFile}
        disabled={!selectedFile || isUploading}
        className={classes.uploadButton}
        aria-label={isUploading ? 'Uploading file...' : 'Upload file'}
      >
        {isUploading ? (
          <span className={classes.buttonContent}>
            <span className={classes.spinner}></span>
            Uploading...
          </span>
        ) : (
          'Upload'
        )}
      </button>
    </div>
  );
};

export default UploadRentRollCsvPM;
